import React from 'react';
import SlidingSegmentedControl from '../Reusables/SlidingSegmentedControl';

/** A quick action that navigates straight to a destination. */
export type QuickActionTile = {
  key: string;
  label: string;
  onPress: () => void;
  renderIcon: (tint?: string) => React.ReactNode;
  color: string;
};

/**
 * A quick action that stands for a set of related destinations rather than a
 * destination of its own. Rendered as a segment whose tiles are `children`.
 */
export type QuickActionGroup = {
  key: string;
  label: string;
  children: QuickActionTile[];
};

export type QuickActionItem = QuickActionTile | QuickActionGroup;

export type QuickActionSegment = {
  key: string;
  label: string;
  tiles: QuickActionTile[];
};

export const TOOLS_SEGMENT_KEY = 'tools';

export const isQuickActionGroup = (
  item: QuickActionItem,
): item is QuickActionGroup =>
  Array.isArray((item as QuickActionGroup).children);

/**
 * Groups keep the position the user gave them in Quick Actions settings. Every
 * ungrouped action collapses into one "Tools" segment sitting where the first
 * of them appears, so reordering in settings still drives segment order.
 */
export const buildQuickActionSegments = (
  items: QuickActionItem[],
): QuickActionSegment[] => {
  const segments: QuickActionSegment[] = [];
  let tools: QuickActionSegment | null = null;

  for (const item of items) {
    if (isQuickActionGroup(item)) {
      if (item.children.length > 0) {
        segments.push({
          key: item.key,
          label: item.label,
          tiles: item.children,
        });
      }
      continue;
    }
    if (!tools) {
      tools = { key: TOOLS_SEGMENT_KEY, label: 'Tools', tiles: [] };
      segments.push(tools);
    }
    tools.tiles.push(item);
  }

  return segments;
};

type QuickActionSegmentsProps = {
  segments: QuickActionSegment[];
  activeIndex: number;
  onChange: (index: number) => void;
  disabled?: boolean;
  colors: any;
  /** When monochrome, thumb uses the darker info blue. */
  colorMode?: 'monochrome' | 'colored';
};

const QuickActionSegments = ({
  segments,
  activeIndex,
  onChange,
  disabled,
  colors,
  colorMode = 'colored',
}: QuickActionSegmentsProps) => {
  return (
    <SlidingSegmentedControl
      options={segments.map(segment => ({
        key: segment.key,
        label: segment.label,
      }))}
      activeIndex={activeIndex}
      onChange={onChange}
      disabled={disabled}
      colors={{
        track: colors.neutralTileBg,
        thumb:colors.successEmerald,
        activeLabel: colors.textInverse,
        inactiveLabel: colors.textSecondary,
      }}
    />
  );
};

export default QuickActionSegments;
